import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { apiClient, ApiClientError } from '@/lib/api/client'
import type { Consignment, Inquiry } from '@/lib/api/types'
import type { WhatsAppBeaconProposal, WhatsAppBinding } from '@/lib/api/whatsapp-types'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Combobox } from '@/components/ui/combobox'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { useAsyncData } from '@/src/hooks/useAsyncData'

type ReviewProps = {
  binding: WhatsAppBinding
  inquiries: Inquiry[]
  consignments: Consignment[]
  sendingEnabled: boolean
  onChanged: () => void
  /**
   * A record owner who is not an administrator: they review proposals for their own
   * records, but the group's history and draft requests stay with administrators.
   */
  ownerView?: boolean
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'The request could not be completed.'
}

function fieldLabel(field: string) {
  const label = field.replaceAll('_', ' ')
  return label.charAt(0).toUpperCase() + label.slice(1)
}

function valueLabel(value: unknown): string {
  if (value == null || value === '') return 'Not set'
  if (typeof value !== 'object') return String(value)
  // Product IDs are source references, not useful human-facing field values.
  return JSON.stringify(value, (key, item: unknown) => (key === 'item_id' ? undefined : item), 2)
}

function proposedValueLabel(field: string, value: unknown, before: unknown) {
  if (field !== 'product_requirements' || !Array.isArray(value) || !Array.isArray(before))
    return valueLabel(value)
  return valueLabel(
    value.map((patch: Record<string, unknown>) => {
      const original = before.find(
        (item: Record<string, unknown>) => item.item_id === patch.item_id,
      )
      return { product: original?.name || 'Product item', ...patch }
    }),
  )
}

function targetLabel(type: string, id: string, props: ReviewProps) {
  if (type === 'CUSTOMER') return `Customer ${props.binding.customer_name}`
  if (type === 'INQUIRY') {
    const inquiry = props.inquiries.find((item) => item.id === id)
    return inquiry?.reference_code ? `Inquiry ${inquiry.reference_code}` : 'Inquiry'
  }
  const consignment = props.consignments.find((item) => item.id === id)
  return consignment ? `Consignment ${consignment.code} · ${consignment.title}` : 'Consignment'
}

function ProposalReview({
  proposal,
  onDecided,
  ...props
}: ReviewProps & { proposal: WhatsAppBeaconProposal; onDecided: () => void }) {
  const { binding, sendingEnabled } = props
  const [selected, setSelected] = useState<string[]>([])
  const [body, setBody] = useState(
    typeof proposal.proposedValues.body === 'string' ? proposal.proposedValues.body : '',
  )
  const [approved, setApproved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [decided, setDecided] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { confirm, dialog } = useConfirm()
  const pending = proposal.status === 'PENDING' && !decided
  const disabled = busy || !pending || !binding.active
  const isChange = proposal.kind === 'RECORD_CHANGE'
  // Drafted because the client mentioned or replied to Xtellar; approving it quotes their message.
  const isReply = !isChange && proposal.evidence.some((item) => typeof item.trigger === 'string')
  const target = targetLabel(proposal.targetType, proposal.targetId, props)
  const targetPath =
    proposal.targetType === 'CUSTOMER'
      ? `/customers/${encodeURIComponent(proposal.targetId)}`
      : proposal.targetType === 'INQUIRY'
        ? `/inquiries/${encodeURIComponent(proposal.targetId)}`
        : `/consignments/${encodeURIComponent(proposal.targetId)}`

  async function decide(decision: 'APPLY' | 'REJECT' | 'SEND') {
    if (disabled) return
    if (decision === 'APPLY' && !selected.length) return
    if (decision === 'SEND' && (!sendingEnabled || !approved || !body.trim())) return
    if (
      !(await confirm({
        title:
          decision === 'APPLY'
            ? 'Apply selected fields?'
            : decision === 'SEND'
              ? 'Queue this customer update?'
              : 'Reject this proposal?',
        description:
          decision === 'APPLY'
            ? `Apply ${selected.map(fieldLabel).join(', ')} to ${target}. This closes the proposal; unchecked fields will not be applied.`
            : decision === 'SEND'
              ? `Send the reviewed wording to ${binding.customer_name} in “${binding.name}”. Queueing does not confirm delivery.`
              : 'This closes the proposal without changing a record or sending a message.',
        action:
          decision === 'APPLY'
            ? 'Apply selected fields'
            : decision === 'SEND'
              ? 'Queue approved update'
              : 'Reject proposal',
        destructive: decision === 'REJECT',
      }))
    )
      return
    setBusy(true)
    setError(null)
    try {
      await apiClient.decideWhatsAppBeaconProposal(proposal.id, {
        decision,
        fields: decision === 'APPLY' ? selected : [],
        ...(decision === 'SEND' ? { body: body.trim() } : {}),
      })
      setDecided(true)
      setApproved(false)
      onDecided()
    } catch (error) {
      setApproved(false)
      setError(
        error instanceof ApiClientError && error.status < 500
          ? error.message
          : 'The decision outcome is uncertain. Refresh proposals and the outbound queue before trying again.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <section
      aria-label={proposal.summary}
      className="flex min-w-0 flex-col gap-4 rounded-lg border p-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-medium">{proposal.summary}</h3>
        <Badge variant="outline">{proposal.status}</Badge>
      </div>
      <Link className="text-sm font-medium underline underline-offset-4" to={targetPath}>
        {target}
      </Link>
      {proposal.sourceMessageId && !props.ownerView && (
        <Link
          className="text-sm underline underline-offset-4"
          to={`/settings/whatsapp?${new URLSearchParams({ binding_id: binding.id, message_id: proposal.sourceMessageId })}`}
        >
          View source message
        </Link>
      )}
      {proposal.evidence.length > 0 && (
        <div className="flex flex-col gap-2 text-sm">
          <p className="font-medium">Source evidence</p>
          {proposal.evidence.map((evidence, index) => (
            <div key={index} className="rounded bg-muted p-3">
              {typeof evidence.context_scope === 'string' ? (
                <p>{evidence.context_scope}</p>
              ) : typeof evidence.excerpt === 'string' ? (
                <blockquote className="whitespace-pre-wrap break-words">
                  {evidence.excerpt}
                </blockquote>
              ) : (
                <p>
                  {typeof evidence.record_type === 'string' &&
                  typeof evidence.record_id === 'string'
                    ? targetLabel(evidence.record_type, evidence.record_id, props)
                    : 'Review the linked source before deciding.'}
                </p>
              )}
              {typeof evidence.attachment_id === 'string' && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Includes an attachment interpretation. Verify the original attachment.
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      {isChange ? (
        <FieldGroup>
          {Object.entries(proposal.proposedValues).map(([field, value]) => (
            <div key={field} className="flex flex-col gap-2 rounded border p-3">
              <Field orientation="horizontal">
                <Checkbox
                  id={`beacon-${proposal.id}-${field}`}
                  aria-label={`Apply ${fieldLabel(field)}`}
                  checked={selected.includes(field)}
                  disabled={disabled}
                  onCheckedChange={(checked) =>
                    setSelected((previous) =>
                      checked === true
                        ? [...new Set([...previous, field])]
                        : previous.filter((item) => item !== field),
                    )
                  }
                />
                <FieldLabel htmlFor={`beacon-${proposal.id}-${field}`}>
                  {fieldLabel(field)}
                </FieldLabel>
              </Field>
              <dl className="grid gap-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-muted-foreground">Current value</dt>
                  <dd className="whitespace-pre-wrap break-words">
                    {valueLabel(proposal.beforeValues[field])}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Proposed value</dt>
                  <dd className="whitespace-pre-wrap break-words">
                    {proposedValueLabel(field, value, proposal.beforeValues[field])}
                  </dd>
                </div>
              </dl>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            Only checked fields will be applied. Applying closes this proposal; unchecked changes
            are not applied.
          </p>
          <Button disabled={disabled || !selected.length} onClick={() => void decide('APPLY')}>
            Apply selected fields
          </Button>
        </FieldGroup>
      ) : (
        <FieldGroup>
          <div className="text-sm">
            <p>Customer: {binding.customer_name}</p>
            <p>
              Group: <span>{binding.name}</span>
            </p>
          </div>
          <Field>
            <FieldLabel htmlFor={`beacon-body-${proposal.id}`}>
              {isReply ? 'Reply to the client' : 'Customer update message'}
            </FieldLabel>
            <Textarea
              id={`beacon-body-${proposal.id}`}
              rows={5}
              maxLength={10000}
              value={body}
              disabled={disabled}
              onChange={(event) => {
                setBody(event.target.value)
                setApproved(false)
              }}
            />
            <FieldDescription>
              Review every claim and the exact destination. Nothing is sent automatically.
            </FieldDescription>
          </Field>
          {!sendingEnabled && (
            <p className="text-sm text-muted-foreground">
              Outbound sending is disabled in this environment.
            </p>
          )}
          <Field orientation="horizontal">
            <Checkbox
              id={`beacon-send-${proposal.id}`}
              checked={approved}
              disabled={disabled || !sendingEnabled}
              onCheckedChange={(checked) => setApproved(checked === true)}
            />
            <FieldLabel
              htmlFor={`beacon-send-${proposal.id}`}
              className="[&>span:first-child]:whitespace-normal"
            >
              I reviewed this update and approve sending it to {binding.customer_name} in{' '}
              {binding.name}.
            </FieldLabel>
          </Field>
          <Button
            disabled={disabled || !sendingEnabled || !approved || !body.trim()}
            onClick={() => void decide('SEND')}
          >
            Approve and queue update
          </Button>
        </FieldGroup>
      )}
      {!binding.active && (
        <p className="text-sm text-muted-foreground">
          This group is paused. No decision can be applied or sent.
        </p>
      )}
      <Button variant="outline" disabled={disabled} onClick={() => void decide('REJECT')}>
        Reject proposal
      </Button>
      {error && (
        <Alert variant="destructive">
          <AlertDescription role="alert">{error}</AlertDescription>
        </Alert>
      )}
      {dialog}
    </section>
  )
}

/** Mounted only when the feature is enabled; existing page permissions remain administrator-only. */
export function WhatsAppBeaconReview(props: ReviewProps) {
  const { binding, inquiries, onChanged, ownerView } = props
  const { data, error, loading, reload } = useAsyncData(
    () => apiClient.listWhatsAppBeaconProposals(binding.id),
    [binding.id],
  )
  const [scope, setScope] = useState('customer')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [requestError, setRequestError] = useState<string | null>(null)
  const request = useRef<{ scope: string; key: string } | null>(null)
  const validScope = scope === 'customer' || inquiries.some((inquiry) => inquiry.id === scope)

  async function requestDraft() {
    if (busy || !binding.active) return
    if (!validScope) return
    if (request.current?.scope !== scope) request.current = { scope, key: crypto.randomUUID() }
    setBusy(true)
    setNote(null)
    setRequestError(null)
    try {
      await apiClient.requestWhatsAppBeaconUpdate(binding.id, {
        ...(scope === 'customer' ? {} : { inquiry_id: scope }),
        idempotency_key: request.current.key,
      })
      request.current = null
      setNote(
        'Draft requested. Nothing was sent. Refresh Beacon review after processing completes.',
      )
      reload()
    } catch (error) {
      setRequestError(
        error instanceof ApiClientError && error.status < 500
          ? error.message
          : 'The draft request outcome is uncertain. Retrying the same scope reuses its request key. Refresh before retrying.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Beacon review</CardTitle>
        <CardDescription>
          Review source-grounded changes and draft customer updates. Records and messages need
          explicit approval.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-4">
        <Button variant="outline" disabled={loading} onClick={reload}>
          <RefreshCw data-icon="inline-start" />
          Refresh Beacon review
        </Button>
        {!ownerView && (
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={`beacon-scope-${binding.id}`}>Update scope</FieldLabel>
              <Combobox
                id={`beacon-scope-${binding.id}`}
                aria-label="Update scope"
                value={scope}
                onValueChange={setScope}
                disabled={busy || !binding.active}
                options={[
                  { value: 'customer', label: 'Customer-wide update' },
                  ...inquiries.map((inquiry) => ({
                    value: inquiry.id,
                    label: inquiry.reference_code
                      ? `Inquiry ${inquiry.reference_code}`
                      : 'Inquiry without a reference',
                  })),
                ]}
              />
              <FieldDescription>
                Requesting a draft does not send it. Review the result here before approving.
              </FieldDescription>
            </Field>
            <Button
              variant="outline"
              disabled={busy || !binding.active || !validScope}
              onClick={() => void requestDraft()}
            >
              Request update draft
            </Button>
          </FieldGroup>
        )}
        {note && (
          <p role="status" className="text-sm">
            {note}
          </p>
        )}
        {requestError && (
          <Alert variant="destructive">
            <AlertDescription role="alert">{requestError}</AlertDescription>
          </Alert>
        )}
        {loading && !data && (
          <p className="text-sm text-muted-foreground">Loading Beacon proposals…</p>
        )}
        {error ? (
          <Alert variant="destructive">
            <AlertDescription role="alert">
              {errorMessage(error)} Refresh to load current proposals.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {data?.length === 0 && (
              <p className="text-sm text-muted-foreground">No Beacon proposals need review.</p>
            )}
            {data?.map((proposal) => (
              <ProposalReview
                key={proposal.id}
                {...props}
                proposal={proposal}
                onDecided={() => {
                  reload()
                  onChanged()
                }}
              />
            ))}
          </>
        )}
      </CardContent>
    </Card>
  )
}
